import React, { useState, useEffect } from "react";
import apiClient from "../../api_client";
import { Table, Checkbox, message, Card, Row, Col, Input } from "antd";
import { API_URL } from "../../url_config";
import { NavigationStepsCapNoc } from "../../components/navbars/navbar_cap_noc";
import { Button } from "antd";
import { EditOutlined } from "@ant-design/icons";
import ModifyItemPage from "../../components/modify_page";
import CoquillesTable from "../../components/counter";

const { TextArea } = Input;

// === Style du tableau ===
const tableStyle = document.createElement("style");
tableStyle.innerHTML = `
  .custom-row td {
    border-bottom: 2px solid #d9d9d9 !important;
  }

  .ant-table-thead > tr > th {
    background-color: #f5f5f5 !important;
    font-weight: 600;
    border-bottom: 2px solid #bfbfbf !important;
    color: #333;
  }

  .ant-table-tbody > tr:hover > td {
    background-color: #f0f0f0 !important;
  }

  .ant-table {
    border-collapse: separate !important;
    border-spacing: 0;
  }

  .comment-cell textarea {
    width: 100%;
    resize: none;
    border-radius: 6px;
  }
`;
document.head.appendChild(tableStyle);

// === Composant interne (comment et aide) ===
function TableCoquilleExpandable({ record, onChange, setIsUpdating }) {
  const [comment, setComment] = useState(record.comment || "");
  const [aide, setAide] = useState(record.aide || "");

  useEffect(() => {
    setComment(record.comment || "");
    setAide(record.aide || "");
  }, [record.comment, record.aide]);

  const handleChange = async (field, value) => {
    setIsUpdating(true);
    onChange(record.key, field, value);

    try {
      await apiClient.patch(
        `${API_URL}/validations/validation_coquille_config_dsp/${record.id}/`,
        { [field]: value }
      );
    } catch (error) {
      console.error(error);
      message.error("Erreur lors de la mise à jour");
    } finally {
      setTimeout(() => setIsUpdating(false), 4000);
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <Row gutter={16}>
        <Col span={12}>
          <Card title="commentaire" bordered>
            <TextArea
              rows={4}
              value={comment}
              onChange={(e) => {
                const value = e.target.value;
                setComment(value);
                handleChange("comment", value);
              }}
              placeholder="Ajoute un commentaire technique..."
            />
          </Card>
        </Col>

        <Col span={12}>
          <Card title="Aide (Supervision)" bordered>
            <TextArea
              rows={4}
              value={aide}
              onChange={(e) => {
                const value = e.target.value;
                setAide(value);
                handleChange("aide", value);
              }}
              placeholder="Décris ton besoin d’aide..."
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

// === Composant principal ===
const ValidationCoquilleConfigDspCapNoc = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // === Chargement des données ===
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (isUpdating) return;

      try {
        const res = await apiClient.get(
          `${API_URL}/validations/validation_coquille_config_dsp/?ot_tag=cap_noc`
        );
        if (!isMounted) return;

        const formatted = res.data.map((item, index) => ({
          key: index,
          id: item.id,
          dsp: item.dsp,
          coquille: item.coquille,
          validation_sortie_coquille: item.validation_sortie_coquille,
          commentaire: item.commentaire,
          comment: item.comment,
          aide: item.aide,
          signal: item.signal,
          ot_tag: item.ot_tag,
        }));

        setData(formatted);
      } catch (error) {
        console.error("Erreur de chargement :", error);
        message.error("Erreur de chargement des données");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    setLoading(true);
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [isUpdating]);

  // === Mise à jour ===
  const handleDataChange = async (key, field, value) => {
    setIsUpdating(true);
    setData((prev) =>
      prev.map((item) => (item.key === key ? { ...item, [field]: value } : item))
    );

    try {
      const updatedItem = data.find((item) => item.key === key);
      if (updatedItem) {
        await apiClient.patch(
          `${API_URL}/validations/validation_coquille_config_dsp/${updatedItem.id}/`,
          { [field]: value }
        );
      }
    } catch (error) {
      console.error(error);
      message.error("Erreur de mise à jour");
    } finally {
      setTimeout(() => setIsUpdating(false), 2000);
    }
  };

  // === Colonnes du tableau ===
  const columns = [
    { title: "DSP", dataIndex: "dsp" },
    { title: "Coquille", dataIndex: "coquille" },
    {
      title: "Test radio fait",
      dataIndex: "validation_sortie_coquille",
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(
              record.key,
              "validation_sortie_coquille",
              e.target.checked
            )
          }
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.3)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    {
      title: "Commentaire",
      dataIndex: "commentaire",
      className: "comment-cell",
      render: (val, record) => (
        <TextArea
          rows={2}
          value={val}
          onChange={(e) =>
            handleDataChange(record.key, "commentaire", e.target.value)
          }
          placeholder="Saisis un commentaire..."
        />
      ),
    },
    // { title: "Utilisateur", dataIndex: "ot_tag" },
    {
      title: "État",
      dataIndex: "signal",
      render: (val, record) => {
        const color =
          val === 1 ? "#4CAF50" : val === -1 ? "#FF4D4F" : "#BFBFBF";
        const symbol = val === 1 ? "✓" : val === -1 ? "✗" : " ";

        const handleClick = () => {
          const nextVal = val === 0 ? 1 : val === 1 ? -1 : 0;
          handleDataChange(record.key, "signal", nextVal);
        };

        return (
          <div
            onClick={handleClick}
            style={{
              cursor: "pointer",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: "6px",
                backgroundColor: color,
                color: "white",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontWeight: "bold",
                fontSize: "16px",
                userSelect: "none",
                transition: "all 0.3s ease",
              }}
            >
              {symbol}
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <NavigationStepsCapNoc selectedKey="coquille_config_dsp" />
      <CoquillesTable url={`${API_URL}/validations/counter/tests_ad/?ot_tag=cap_noc`} />
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        expandable={{
          expandedRowRender: (record) => (
            <TableCoquilleExpandable
              record={record}
              onChange={handleDataChange}
              setIsUpdating={setIsUpdating}
            />
          ),
        }}
        bordered
        style={{
          border: "2px solid #bfbfbf",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        }}
        rowClassName={() => "custom-row"}
        pagination={false}
      />

      <div style={{ padding: 20 }}>

            <ModifyItemPage
              apiUrl={`${API_URL}/validations/validation_coquille_config_dsp/`}
              ot_tag="cap_noc"
              title="Gestion"
              rowKey="id"
              trigger={<Button icon={<EditOutlined />} shape="circle" />}
              columns={[
                { title: "Dsp", dataIndex: "dsp", key: "dsp" },
                { title: "Coquille", dataIndex: "coquille", key: "coquille" },
              ]}
              formFields={[
                { name: "dsp", label: "Dsp", placeholder: "Entrez la Dsp" },
                { name: "coquille", label: "Coquille", placeholder: "Entrez la coquille" },
              ]}
            />
      </div>
    </div>
  );
};

export default ValidationCoquilleConfigDspCapNoc;
